import { useQuery } from '@tanstack/react-query';

import { payScheduleQueryKeys } from '@xemplo/gp-constants';
import {
  BasePayMapping,
  ListResultV2,
  PayScheduleDetail,
  PayScheduleListItem,
  PayScheduleProcessingFee,
  RequestParamsV2,
  ResultV2,
} from '@xemplo/gp-types';
import { useHttpClient } from '@xemplo/http';
import { apiUrlBuilder } from '@xemplo/url-helper';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import { PayScheduleAPIV2 } from './pay-schedule-query.constants';
import { payScheduleId } from './pay-schedule-query.types';

/** Get a list of pay schedules based on the optional query filter. ie. filter=companyId:{id} */
export const useGetPayschedulesQuery = (props: RequestParamsV2) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const url = apiUrlBuilder(PayScheduleAPIV2.paySchedules, applicationApiUrl, props);

  return useQuery({
    queryKey: payScheduleQueryKeys.list(url),
    queryFn: ({ signal }) =>
      client
        .get<ListResultV2<PayScheduleListItem>>(url, { signal })
        .then((resp) => resp.data),
    enabled: !!applicationApiUrl,
    select: (data) => {
      const payScheduleItems = data.result?.rows.map((row) => ({
        key: row.id,
        value: row.name,
      }));
      return { originalData: data, payScheduleItems };
    },
  });
};

/** Get Payschedule details by Payschedule Id */
export const useGetPayscheduleByIdQuery = (props: { id: payScheduleId }) => {
  const { id: payScheduleId } = props;
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const url = apiUrlBuilder(
    PayScheduleAPIV2.payScheduleById(payScheduleId),
    applicationApiUrl,
  );

  return useQuery({
    queryKey: payScheduleQueryKeys.detail(String(payScheduleId), url),
    queryFn: ({ signal }) =>
      client.get<ResultV2<PayScheduleDetail>>(url, { signal }).then((resp) => resp.data),
    enabled: !!applicationApiUrl && !!payScheduleId,
  });
};

/** Get base pay mapping of a payschedule. */
export const useGetBasePayMappingByIdQuery = (props: { id: payScheduleId }) => {
  const { id: payScheduleId } = props;
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const url = apiUrlBuilder(
    PayScheduleAPIV2.basePayMappingById(payScheduleId),
    applicationApiUrl,
  );

  return useQuery({
    queryKey: [payScheduleQueryKeys.baseMapping(String(payScheduleId), url)],
    queryFn: ({ signal }) =>
      client.get<ResultV2<BasePayMapping[]>>(url, { signal }).then((resp) => resp.data),
    enabled: !!applicationApiUrl && !!payScheduleId,
  });
};

/**  Get processing fees of a payschedule. */
export const useGetFeesByIdQuery = (props: { id: payScheduleId }) => {
  const { id: payScheduleId } = props;
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const url = apiUrlBuilder(PayScheduleAPIV2.feesById(payScheduleId), applicationApiUrl);

  return useQuery({
    queryKey: payScheduleQueryKeys.fees(String(payScheduleId), url),
    queryFn: ({ signal }) =>
      client
        .get<ResultV2<PayScheduleProcessingFee[]>>(url, { signal })
        .then((resp) => resp.data),
    enabled: !!applicationApiUrl && !!payScheduleId,
  });
};
